// @flow

import { createStore, applyMiddleware, combineReducers, compose } from 'redux'
import thunk from 'redux-thunk'

import { authenticationReducer, middleware as authenticationMiddleware } from './authentication'
import { reducer as routerReducer, enhancer as routerEnhancer, middleware as routerMiddleware } from './router'
import appReducer from './app'
import campaignsReducer from './campaigns'
import allowedCampaignsReducer from './allowed-campaigns'
import notificationReducer from './notification-manager'
import usersReducer from './manage-users'
import panelReducer from './manage-panel'
import visitsReducer from './manage-visits'
import xlsxUploaderReducer from './xlsx-uploader'
import reportReducer from './reports'

import type { ManageUsersState, ManageUsersAction } from './manage-users'
import type { ManagePanelState, ManagePanelAction } from './manage-panel'
import type { ManageVisitsState, ManageVisitsAction } from './manage-visits'
import type { CampaignsState, CampaignsAction } from './campaigns'
import type { AllowedCampaignsState, AllowedCampaignsAction } from './allowed-campaigns'
import type { AuthenticationState, AuthenticationAction } from './authentication'
import type { NotificationManagerState, NotificationManagerAction } from './notification-manager'
import type { XLSXUploaderState, XLSXUploaderAction } from './xlsx-uploader'
import type { ReportsSectionState, ReportAction } from './reports.js'
import type { AppState, AppAction } from './app'
import type { RouterState, RouterAction } from './router'
import type { Dispatch as ReduxDispatch, Reducer, StoreEnhancer } from 'redux'

export type ReduxState = {
  app: AppState,
  auth: AuthenticationState,
  location: RouterState,
  notificationManager: NotificationManagerState,
  campaigns: CampaignsState,
  allowedCampaigns: AllowedCampaignsState,
  users: ManageUsersState,
  xlsxUploader: XLSXUploaderState,
  panel: ManagePanelState,
  visits: ManageVisitsState,
  reports: ReportsSectionState,
}

type ACTION_CLEAR_STORE = {| type: 'CLEAR_STORE' |}
type ACTION_NOOP = {| type: 'NOOP' |}
export type ReduxAction =
  | AppAction
  | AuthenticationAction
  | RouterAction
  | NotificationManagerAction
  | CampaignsAction
  | AllowedCampaignsAction
  | ManageUsersAction
  | XLSXUploaderAction
  | ManagePanelAction
  | ManageVisitsAction
  | ReportAction
  | ACTION_CLEAR_STORE
  | ACTION_NOOP

export type GetState = () => ReduxState
export type Thunk<A> = ((Dispatch, GetState) => Promise<void> | void) => A
export type Dispatch = ReduxDispatch<ReduxAction> & Thunk<ReduxAction>

const composeEnhancers =
  typeof window === 'object' && process.env.NODE_ENV === 'development' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
    ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({})
    : compose

const reducers = combineReducers({
  app: appReducer,
  auth: authenticationReducer,
  location: routerReducer,
  notificationManager: notificationReducer,
  campaigns: campaignsReducer,
  allowedCampaigns: allowedCampaignsReducer,
  users: usersReducer,
  xlsxUploader: xlsxUploaderReducer,
  panel: panelReducer,
  visits: visitsReducer,
  reports: reportReducer,
})

const rootReducer: Reducer<ReduxState, ReduxAction> = (state: ReduxState, action: ReduxAction) => {
  if (action.type === 'CLEAR_STORE') {
    // $FlowFixMe
    state = undefined
  }
  return reducers(state, action)
}

const middlewares = applyMiddleware(authenticationMiddleware, routerMiddleware, thunk)
// $FlowFixMe
const enhancers: StoreEnhancer<ReduxState, ReduxAction, Dispatch> = composeEnhancers(routerEnhancer, middlewares)

export const store = createStore(rootReducer, enhancers)
export default store



// WEBPACK FOOTER //
// ./src/store/index.js